import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.css';

interface TeamMember {
  id: string;
  nickname: string;
  avatar: string;
  level: number;
  joinTime: string;
  status: 'active' | 'inactive';
}

interface TeamStats {
  totalMembers: number;
  directMembers: number;
  level1Members: number;
  level2Members: number;
  level3Members: number;
}

const Team: React.FC = () => {
  const navigate = useNavigate();
  const [teamStats, setTeamStats] = useState<TeamStats>({
    totalMembers: 0,
    directMembers: 0,
    level1Members: 0,
    level2Members: 0,
    level3Members: 0
  });
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [activeLevel, setActiveLevel] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState<string>('XSWVR2');

  useEffect(() => {
    // 从URL获取token参数
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    
    if (!tokenParam) {
      alert('请先登录');
      navigate('/');
      return;
    }

    loadTeamData(tokenParam);
  }, [navigate]);

  const loadTeamData = async (token: string) => {
    try {
      // TODO: 调用后端API获取团队数据
      // 模拟数据
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setTeamStats({
        totalMembers: 0,
        directMembers: 0,
        level1Members: 0,
        level2Members: 0,
        level3Members: 0
      });

      // setTeamMembers([
      //   {
      //     id: '1',
      //     nickname: '张三',
      //     avatar: '/icons/avatar.png',
      //     level: 1,
      //     joinTime: '2024-01-15',
      //     status: 'active'
      //   },
      //   {
      //     id: '2',
      //     nickname: '李四',
      //     avatar: '/icons/avatar.png',
      //     level: 1,
      //     joinTime: '2024-01-20',
      //     status: 'active'
      //   },
      //   {
      //     id: '3',
      //     nickname: '王五',
      //     avatar: '/icons/avatar.png',
      //     level: 2,
      //     joinTime: '2024-02-01',
      //     status: 'active'
      //   }
      // ]);
    } catch (error) {
      console.error('加载团队数据失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getInviteCode = () => {
    // TODO: 从后端获取邀请码
    return 'XSWVR2';
  };

  // 处理复制邀请码
  const handleCopyInviteCode = async () => {
    try {
      await navigator.clipboard.writeText(inviteCode);
      alert('邀请码已复制到剪贴板');
    } catch (error) {
      // 如果剪贴板API不可用，使用传统方法
      const textArea = document.createElement('textarea');
      textArea.value = inviteCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('邀请码已复制到剪贴板');
    }
  };

  const getLevelMembers = (level: number) => {
    return teamMembers.filter(member => member.level === level);
  };

  const getLevelLabel = (level: number) => {
    switch (level) {
      case 1: return '一级';
      case 2: return '二级';
      case 3: return '三级';
      default: return `${level}级`;
    }
  };

  const renderTeamStats = () => (
    <div className="team-stats">
      <div className="stat-card">
        <div className="stat-icon">👥</div>
        <div className="stat-content">
          <div className="stat-value">{teamStats.totalMembers}</div>
          <div className="stat-label">团队总人数</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon">👤</div>
        <div className="stat-content">
          <div className="stat-value">{teamStats.directMembers}</div>
          <div className="stat-label">直推人数</div>
        </div>
      </div>
    </div>
  );

  const renderLevelTabs = () => (
    <div className="level-tabs">
      {[1, 2, 3].map(level => (
        <button
          key={level}
          className={`level-tab ${activeLevel === level ? 'active' : ''}`}
          onClick={() => setActiveLevel(level)}
        >
          {getLevelLabel(level)} ({getLevelMembers(level).length})
        </button>
      ))}
    </div>
  );

  const renderTeamList = () => {
    const currentMembers = getLevelMembers(activeLevel);
    
    if (currentMembers.length === 0) {
      return (
        <div className="empty-list">
          <div className="empty-icon">📭</div>
          <div className="empty-text">暂无{getLevelLabel(activeLevel)}成员</div>
        </div>
      );
    }

    return (
      <div className="team-list">
        {currentMembers.map(member => (
          <div key={member.id} className="team-member">
            <div className="member-avatar">
              <img src={member.avatar} alt={member.nickname} />
            </div>
            <div className="member-info">
              <div className="member-name">{member.nickname}</div>
              <div className="member-join-time">加入时间: {member.joinTime}</div>
            </div>
            <div className="member-status">
              <span className={`status-badge ${member.status}`}>
                {member.status === 'active' ? '活跃' : '非活跃'}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleBackClick = () => {
    // 获取当前URL参数，保持用户登录状态
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const nicknameParam = urlParams.get('nickname');
    const avatarParam = urlParams.get('avatar');
    const balanceParam = urlParams.get('balance');
    
    const backUrl = `/?token=${tokenParam}&nickname=${nicknameParam}&avatar=${avatarParam}&balance=${balanceParam}`;
    navigate(backUrl);
  };

  if (isLoading) {
    return (
      <div className="team-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">加载中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="team-container">
      {/* 头部邀请码区域 */}
      <div className="invite-code-section">
        <div className="section-header">
          <h2>我的邀请码</h2>
          <button className="back-btn" onClick={handleBackClick}>
            ← 返回
          </button>
        </div>
        <div className="invite-code-card">
          <div className="invite-code-display">
            <span className="code-text">{inviteCode}</span>
          </div>
          <button className="copy-btn" onClick={handleCopyInviteCode}>
            <span className="copy-icon">📋</span>
            复制邀请码
          </button>
        </div>
      </div>

      {/* 团队统计区域 */}
      <div className="stats-section">
        <h3>团队统计</h3>
        {renderTeamStats()}
      </div>

      {/* 团队成员列表区域 */}
      <div className="members-section">
        <h3>团队成员</h3>
        {renderLevelTabs()}
        {renderTeamList()}
      </div>
    </div>
  );
};

export default Team; 